import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createProspect, getProspect, updateProspect, getSettings, getProspects, getUsers } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/SearchableSelect';
import { ArrowLeft, Loader2, UserCircle, Calendar as CalendarIcon, Gift } from 'lucide-react';
import { toast } from 'sonner';

export const ProspectFormPage = () => {
    const { id } = useParams();
    const isEdit = !!id;
    const navigate = useNavigate();
    const { user } = useAuth();
    const canAssign = user?.role === 'admin' || user?.role === 'manager';
    
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({ types_travaux: [], sources_lead: [] });
    const [prospects, setProspects] = useState([]);
    const [commerciaux, setCommerciaux] = useState([]);
    
    const [form, setForm] = useState(() => {
        // Read query params synchronously so a controlled Radix Select renders
        // the correct value on the very first paint (fixes 'Nouveau syndic' —
        // ?type_contact=syndic previously rendered a blank Select and defaulted
        // the submitted payload back to 'particulier').
        const q = new URLSearchParams(window.location.search);
        const typeFromQuery = q.get('type_contact');
        const initialType = ['particulier', 'professionnel', 'sci', 'syndic'].includes(typeFromQuery)
            ? typeFromQuery
            : 'particulier';
        return {
            nom: '',
            prenom: '',
            societe: '',
            siret: '',
            telephone: '',
            telephone_2: '',
            email: '',
            email_2: '',
            adresse: '',
            ville: '',
            code_postal: '',
            adresse_livraison: '',
            ville_livraison: '',
            code_postal_livraison: '',
            type_contact: initialType,
            source_lead: '',
            nature_besoin: '',
            nature_projet: '',
            statut_projet: '',
            type_travaux: '',
            commentaire: '',
            parrain_id: q.get('parrain_id') || '',
            commercial_id: '',
            date_rdv: '',
            heure_rdv: '',
            parent_prospect_id: q.get('parent_prospect_id') || '',
        };
    });
    const [sameDeliveryAddress, setSameDeliveryAddress] = useState(true);

    const [searchParams] = useSearchParams();

    useEffect(() => {
        fetchSettings();
        fetchProspects();
        if (canAssign) fetchCommerciaux();
        if (isEdit) {
            fetchProspect();
        }
    }, [id, canAssign]);

    // Prefill form from query params (used when arriving from the Syndics page or "Parrainage 1 clic")
    useEffect(() => {
        if (isEdit) return;
        const typeFromQuery = searchParams.get('type_contact');
        const parentFromQuery = searchParams.get('parent_prospect_id');
        const parrainFromQuery = searchParams.get('parrain_id');
        if (!typeFromQuery && !parentFromQuery && !parrainFromQuery) return;
        setForm((prev) => {
            const next = { ...prev };
            if (typeFromQuery && ['particulier', 'professionnel', 'sci', 'syndic'].includes(typeFromQuery)) {
                next.type_contact = typeFromQuery;
            }
            if (parentFromQuery) {
                next.parent_prospect_id = parentFromQuery;
            }
            if (parrainFromQuery) {
                next.parrain_id = parrainFromQuery;
            }
            return next;
        });
    }, [isEdit, searchParams]);

    const fetchSettings = async () => {
        try {
            const res = await getSettings();
            setSettings(res.data);
        } catch (error) {
            console.error('Error fetching settings:', error);
        }
    };

    const fetchProspects = async () => {
        try {
            // Get all prospects/clients (no is_client filter) so a parrain
            // can be either a confirmed client OR an existing prospect
            const res = await getProspects();
            // Sort: clients first, then prospects, both alphabetically
            const sorted = (res.data || []).sort((a, b) => {
                if (!!a.is_client !== !!b.is_client) return a.is_client ? -1 : 1;
                const aName = `${a.prenom || ''} ${a.nom || ''}`.trim().toLowerCase();
                const bName = `${b.prenom || ''} ${b.nom || ''}`.trim().toLowerCase();
                return aName.localeCompare(bName);
            });
            setProspects(sorted);
        } catch (error) {
            console.error('Error fetching prospects:', error);
        }
    };

    const fetchCommerciaux = async () => {
        try {
            const res = await getUsers();
            setCommerciaux(res.data.filter(u => ['commercial', 'manager', 'admin'].includes(u.role)));
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchProspect = async () => {
        setLoading(true);
        try {
            const res = await getProspect(id);
            setForm({
                nom: res.data.nom || '',
                prenom: res.data.prenom || '',
                societe: res.data.societe || '',
                siret: res.data.siret || '',
                telephone: clampPhone(res.data.telephone || ''),
                telephone_2: clampPhone(res.data.telephone_2 || ''),
                email: res.data.email || '',
                email_2: res.data.email_2 || '',
                adresse: res.data.adresse || '',
                ville: res.data.ville || '',
                code_postal: res.data.code_postal || '',
                adresse_livraison: res.data.adresse_livraison || '',
                ville_livraison: res.data.ville_livraison || '',
                code_postal_livraison: res.data.code_postal_livraison || '',
                type_contact: res.data.type_contact || 'particulier',
                source_lead: res.data.source_lead || '',
                nature_besoin: res.data.nature_besoin || '',
                nature_projet: res.data.nature_projet || '',
                statut_projet: res.data.statut_projet || '',
                type_travaux: res.data.type_travaux || '',
                commentaire: res.data.commentaire || '',
                parrain_id: res.data.parrain_id || '',
                commercial_id: res.data.commercial_id || ''
            });
            // If a delivery address exists and differs from billing, show the secondary block
            const hasDeliv = !!(res.data.adresse_livraison || res.data.ville_livraison || res.data.code_postal_livraison);
            setSameDeliveryAddress(!hasDeliv);
        } catch (error) {
            toast.error('Erreur lors du chargement');
            navigate('/prospects');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    /**
     * Sanitize + format a phone-number input as the user types:
     *  - accept only digits, `+`, spaces, dots, dashes
     *  - cap total DIGIT count at 10 (FR) — or 15 if the value starts with `+`
     *    (E.164 international)
     *  - auto-format FR numbers by 2s: "0612345678" → "06 12 34 56 78"
     *  - international values (starting with `+`) are NOT auto-formatted so the
     *    user keeps full control over country codes / spacing.
     */
    const clampPhone = (raw) => {
        const cleaned = (raw || '').replace(/[^0-9+\s.-]/g, '');
        const startsIntl = cleaned.charAt(0) === '+';

        if (startsIntl) {
            // International — cap 15 digits, keep user spacing
            const normalized = '+' + cleaned.slice(1).replace(/\+/g, '');
            let digitCount = 0;
            let out = '';
            for (const ch of normalized) {
                if (/\d/.test(ch)) {
                    if (digitCount >= 15) continue;
                    digitCount += 1;
                }
                out += ch;
            }
            return out.slice(0, 20);
        }

        // FR — strip everything except digits, cap 10, format by groups of 2
        const digits = cleaned.replace(/\D/g, '').slice(0, 10);
        return digits.match(/.{1,2}/g)?.join(' ') || '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Guard against double-fire (StrictMode, double-clicks, or browser default submit races)
        if (saving) return;

        if (!form.nom || !form.prenom || !form.telephone) {
            toast.error('Veuillez remplir les champs obligatoires');
            return;
        }

        // RDV required for commercials on creation
        if (!isEdit && !canAssign && (!form.date_rdv || !form.heure_rdv)) {
            toast.error('La prise de rendez-vous (date et heure) est obligatoire');
            return;
        }

        setSaving(true);
        try {
            const data = { ...form };
            // If the same address is used for delivery, clear the delivery fields
            if (sameDeliveryAddress) {
                data.adresse_livraison = '';
                data.ville_livraison = '';
                data.code_postal_livraison = '';
            }
            // SIRET is only relevant for professionnels/syndic
            if (!['professionnel', 'sci', 'syndic'].includes(data.type_contact)) {
                data.siret = '';
            }
            // Remove empty optional fields
            Object.keys(data).forEach(key => {
                if (data[key] === '') delete data[key];
            });

            if (isEdit) {
                // Don't send RDV fields on edit
                delete data.date_rdv;
                delete data.heure_rdv;
                await updateProspect(id, data);
                toast.success('Prospect mis à jour');
            } else {
                const res = await createProspect(data);
                if (data.date_rdv && data.heure_rdv) {
                    toast.success('Prospect créé avec RDV planifié');
                } else {
                    toast.success('Prospect créé');
                }
            }
            navigate('/prospects');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#E85A24]" />
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto" data-testid="prospect-form-page">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => navigate(-1)}
                    className="rounded-sm"
                    data-testid="back-btn"
                >
                    <ArrowLeft className="w-5 h-5" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-[#0A0A0A] tracking-tight">
                        {isEdit ? 'Modifier le prospect' : 'Nouveau prospect'}
                    </h1>
                    <p className="text-[#52525B]">
                        {isEdit ? 'Modifiez les informations du prospect' : 'Créez une nouvelle fiche prospect'}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Parrain pre-attached banner (from "Parrainage 1 clic" flow) */}
                {!isEdit && form.parrain_id && (() => {
                    const parrain = prospects.find((p) => p.id === form.parrain_id);
                    if (!parrain) return null;
                    const label = `${parrain.prenom || ''} ${parrain.nom || ''}`.trim() || parrain.societe || 'Contact';
                    return (
                        <div
                            className="flex items-start gap-3 bg-[#E85A24]/5 border-2 border-[#E85A24] p-4 rounded-sm"
                            data-testid="parrain-prefilled-banner"
                        >
                            <Gift className="w-5 h-5 text-[#E85A24] mt-0.5 flex-shrink-0" />
                            <div className="text-sm">
                                <div className="font-semibold text-[#0A0A0A]">Parrainage attaché</div>
                                <div className="text-[#52525B]">
                                    Ce nouveau contact sera lié à <span className="font-semibold text-[#0A0A0A]">{label}</span>{parrain.societe ? ` (${parrain.societe})` : ''}. Vous pouvez le modifier plus bas dans le formulaire.
                                </div>
                            </div>
                        </div>
                    );
                })()}
                {/* Commercial assigné - Visible uniquement pour admin/manager */}
                {canAssign && (
                <div className="bg-[#7C3AED]/5 border-2 border-[#7C3AED] p-6" data-testid="commercial-assignment-block">
                    <div className="flex items-center gap-3 mb-4">
                        <UserCircle className="w-6 h-6 text-[#7C3AED]" />
                        <h2 className="text-lg font-semibold text-[#0A0A0A]">Commercial responsable</h2>
                    </div>
                    <div className="space-y-2">
                        <Label className="field-label">Assigner à un commercial *</Label>
                        <Select value={form.commercial_id || 'none'} onValueChange={(v) => handleChange('commercial_id', v === 'none' ? '' : v)}>
                            <SelectTrigger className="h-12 rounded-sm border-[#7C3AED] bg-white" data-testid="select-commercial">
                                <SelectValue placeholder="Sélectionner un commercial" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">Non assigné</SelectItem>
                                {commerciaux.map(c => (
                                    <SelectItem key={c.id} value={c.id}>
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-full bg-[#7C3AED] flex items-center justify-center text-white text-xs font-bold">
                                                {c.name.charAt(0)}
                                            </div>
                                            {c.name} <span className="text-[#52525B]">({c.role})</span>
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-sm text-[#52525B]">
                            Le commercial sera responsable du suivi de ce prospect.
                        </p>
                    </div>
                </div>
                )}
                {/* Informations personnelles */}
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <h2 className="text-lg font-semibold text-[#0A0A0A] mb-4">Informations personnelles</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="field-label">Prénom *</Label>
                            <Input
                                value={form.prenom}
                                onChange={(e) => handleChange('prenom', e.target.value)}
                                placeholder="Jean"
                                className="h-11 rounded-sm"
                                required
                                data-testid="input-prenom"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="field-label">Nom *</Label>
                            <Input
                                value={form.nom}
                                onChange={(e) => handleChange('nom', e.target.value)}
                                placeholder="Dupont"
                                className="h-11 rounded-sm"
                                required
                                data-testid="input-nom"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="field-label">Société</Label>
                            <Input
                                value={form.societe}
                                onChange={(e) => handleChange('societe', e.target.value)}
                                placeholder="Nom de l'entreprise"
                                className="h-11 rounded-sm"
                                data-testid="input-societe"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="field-label">Type de contact</Label>
                            <Select value={form.type_contact} onValueChange={(v) => handleChange('type_contact', v)}>
                                <SelectTrigger className="h-11 rounded-sm" data-testid="select-type-contact">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="particulier">Particulier</SelectItem>
                                    <SelectItem value="professionnel">Professionnel</SelectItem>
                                    <SelectItem value="sci">SCI</SelectItem>
                                    <SelectItem value="syndic">Syndic / Copropriété</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {['professionnel', 'sci', 'syndic'].includes(form.type_contact) && (
                            <div className="space-y-2 md:col-span-2">
                                <Label className="field-label">SIRET / Numéro d&apos;immatriculation</Label>
                                <Input
                                    value={form.siret}
                                    onChange={(e) => handleChange('siret', e.target.value)}
                                    placeholder="123 456 789 00012"
                                    className="h-11 rounded-sm"
                                    data-testid="input-siret"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Coordonnées */}
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <h2 className="text-lg font-semibold text-[#0A0A0A] mb-4">Coordonnées</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="field-label">Téléphone *</Label>
                            <Input
                                value={form.telephone}
                                onChange={(e) => handleChange('telephone', clampPhone(e.target.value))}
                                placeholder="06 12 34 56 78"
                                inputMode="tel"
                                maxLength={20}
                                className="h-11 rounded-sm"
                                required
                                data-testid="input-telephone"
                            />
                            <p className="text-[11px] text-[#A1A1AA]">Format auto : 06 12 34 56 78 (ou +33… pour l&apos;international)</p>
                        </div>
                        <div className="space-y-2">
                            <Label className="field-label">Téléphone 2 (optionnel)</Label>
                            <Input
                                value={form.telephone_2}
                                onChange={(e) => handleChange('telephone_2', clampPhone(e.target.value))}
                                placeholder="01 23 45 67 89"
                                inputMode="tel"
                                maxLength={20}
                                className="h-11 rounded-sm"
                                data-testid="input-telephone-2"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="field-label">Email</Label>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                                placeholder="email@exemple.fr"
                                className="h-11 rounded-sm"
                                data-testid="input-email"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="field-label">Email 2 (optionnel)</Label>
                            <Input
                                type="email"
                                value={form.email_2}
                                onChange={(e) => handleChange('email_2', e.target.value)}
                                placeholder="second@exemple.fr"
                                className="h-11 rounded-sm"
                                data-testid="input-email-2"
                            />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label className="field-label">Adresse de facturation</Label>
                            <Input
                                value={form.adresse}
                                onChange={(e) => handleChange('adresse', e.target.value)}
                                placeholder="123 rue de la Paix"
                                className="h-11 rounded-sm"
                                data-testid="input-adresse"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="field-label">Code postal</Label>
                            <Input
                                value={form.code_postal}
                                onChange={(e) => handleChange('code_postal', e.target.value)}
                                placeholder="75001"
                                className="h-11 rounded-sm"
                                data-testid="input-code-postal"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="field-label">Ville</Label>
                            <Input
                                value={form.ville}
                                onChange={(e) => handleChange('ville', e.target.value)}
                                placeholder="Paris"
                                className="h-11 rounded-sm"
                                data-testid="input-ville"
                            />
                        </div>
                        <div className="md:col-span-2 pt-2 border-t border-[#E4E4E7]">
                            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={sameDeliveryAddress}
                                    onChange={(e) => setSameDeliveryAddress(e.target.checked)}
                                    className="h-4 w-4 rounded-sm border-[#A1A1AA]"
                                    data-testid="toggle-same-delivery-address"
                                />
                                <span>L&apos;adresse de livraison/chantier est identique à l&apos;adresse de facturation</span>
                            </label>
                        </div>
                        {!sameDeliveryAddress && (
                            <>
                                <div className="space-y-2 md:col-span-2">
                                    <Label className="field-label">Adresse de livraison / chantier</Label>
                                    <Input
                                        value={form.adresse_livraison}
                                        onChange={(e) => handleChange('adresse_livraison', e.target.value)}
                                        placeholder="456 avenue du Chantier"
                                        className="h-11 rounded-sm"
                                        data-testid="input-adresse-livraison"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="field-label">Code postal (livraison)</Label>
                                    <Input
                                        value={form.code_postal_livraison}
                                        onChange={(e) => handleChange('code_postal_livraison', e.target.value)}
                                        placeholder="75002"
                                        className="h-11 rounded-sm"
                                        data-testid="input-code-postal-livraison"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="field-label">Ville (livraison)</Label>
                                    <Input
                                        value={form.ville_livraison}
                                        onChange={(e) => handleChange('ville_livraison', e.target.value)}
                                        placeholder="Paris"
                                        className="h-11 rounded-sm"
                                        data-testid="input-ville-livraison"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Informations commerciales */}
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <h2 className="text-lg font-semibold text-[#0A0A0A] mb-4">Informations commerciales</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="field-label">Source du lead</Label>
                            <Select value={form.source_lead || 'none'} onValueChange={(v) => handleChange('source_lead', v === 'none' ? '' : v)}>
                                <SelectTrigger className="h-11 rounded-sm" data-testid="select-source">
                                    <SelectValue placeholder="Sélectionner une source" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Non défini</SelectItem>
                                    {settings.sources_lead?.map(source => (
                                        <SelectItem key={source} value={source}>{source}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label className="field-label">Type de travaux</Label>
                            <Select value={form.type_travaux || 'none'} onValueChange={(v) => handleChange('type_travaux', v === 'none' ? '' : v)}>
                                <SelectTrigger className="h-11 rounded-sm" data-testid="select-travaux">
                                    <SelectValue placeholder="Sélectionner un type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Non défini</SelectItem>
                                    {settings.types_travaux?.map(type => (
                                        <SelectItem key={type} value={type}>{type}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label className="field-label">Nature du projet / besoin</Label>
                            <Input
                                value={form.nature_projet}
                                onChange={(e) => handleChange('nature_projet', e.target.value)}
                                placeholder="Ex : rénovation cuisine, isolation combles..."
                                className="h-11 rounded-sm"
                                data-testid="input-nature-projet"
                            />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label className="field-label">Statut du projet</Label>
                            <Select value={form.statut_projet || 'none'} onValueChange={(v) => handleChange('statut_projet', v === 'none' ? '' : v)}>
                                <SelectTrigger className="h-11 rounded-sm" data-testid="select-statut-projet">
                                    <SelectValue placeholder="Non défini" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Non défini</SelectItem>
                                    <SelectItem value="residence_principale">Résidence principale</SelectItem>
                                    <SelectItem value="investissement">Investissement</SelectItem>
                                    <SelectItem value="location">Location</SelectItem>
                                    <SelectItem value="usage_professionnel">Usage professionnel</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label className="field-label">Parrain (si recommandation)</Label>
                            <SearchableSelect
                                options={prospects
                                    .filter((p) => p.id !== id)
                                    .map((p) => ({
                                        value: p.id,
                                        label: `${p.prenom || ''} ${p.nom || ''}`.trim() || '—',
                                        sublabel: [
                                            p.societe,
                                            p.ville,
                                            p.telephone,
                                            p.email,
                                            p.is_client ? 'Client' : 'Prospect',
                                        ].filter(Boolean).join(' · '),
                                        keywords: `${p.email || ''} ${p.telephone || ''} ${p.societe || ''} ${p.ville || ''} ${p.code_postal || ''}`,
                                    }))}
                                value={form.parrain_id || ''}
                                onChange={(v) => handleChange('parrain_id', v || '')}
                                placeholder={prospects.filter((p) => p.id !== id).length === 0
                                    ? 'Aucun client/prospect dans votre portefeuille pour l\'instant'
                                    : 'Rechercher un parrain (nom, société, téléphone, email...)'}
                                emptyMsg="Aucun parrain trouvé"
                                allowClear={true}
                                testId="select-parrain"
                            />
                            <p className="text-xs text-[#52525B] mt-1">
                                Sélectionnez un client ou prospect existant qui a recommandé ce nouveau contact.
                                {prospects.filter((p) => p.id !== id).length === 0 && (
                                    <> Commencez par créer votre premier contact — vous pourrez ensuite l&apos;utiliser comme parrain.</>
                                )}
                            </p>
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label className="field-label">Commentaire</Label>
                            <Textarea
                                value={form.commentaire}
                                onChange={(e) => handleChange('commentaire', e.target.value)}
                                placeholder="Notes additionnelles..."
                                rows={3}
                                className="rounded-sm"
                                data-testid="input-commentaire"
                            />
                        </div>
                    </div>
                </div>

                {/* Prise de RDV - obligatoire pour les commerciaux */}
                {!isEdit && (
                    <div className={`border p-6 ${canAssign ? 'border-[#E4E4E7] bg-white' : 'border-[#E85A24] bg-[#E85A24]/5'}`} data-testid="rdv-section">
                        <div className="flex items-center gap-3 mb-4">
                            <CalendarIcon className="w-5 h-5 text-[#E85A24]" />
                            <h2 className="text-lg font-semibold text-[#0A0A0A]">
                                Prise de rendez-vous
                                {!canAssign && <span className="text-[#E85A24] ml-1">*</span>}
                            </h2>
                        </div>
                        <p className="text-sm text-[#52525B] mb-4">
                            {canAssign
                                ? "Optionnel — renseigner la date et l'heure du premier RDV créera automatiquement un dossier au statut 'RDV planifié'."
                                : "Obligatoire — précisez la date et l'heure du premier rendez-vous pour créer le dossier."
                            }
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label className="field-label">Date du RDV{!canAssign && <span className="text-[#E85A24]"> *</span>}</Label>
                                <Input
                                    type="date"
                                    value={form.date_rdv}
                                    onChange={(e) => handleChange('date_rdv', e.target.value)}
                                    className="h-12 rounded-sm mt-1"
                                    data-testid="prospect-date-rdv"
                                    required={!canAssign}
                                />
                            </div>
                            <div>
                                <Label className="field-label">Heure du RDV{!canAssign && <span className="text-[#E85A24]"> *</span>}</Label>
                                <Input
                                    type="time"
                                    value={form.heure_rdv}
                                    onChange={(e) => handleChange('heure_rdv', e.target.value)}
                                    className="h-12 rounded-sm mt-1"
                                    data-testid="prospect-heure-rdv"
                                    required={!canAssign}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3">
                    <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => navigate(-1)}
                        className="rounded-sm"
                        data-testid="cancel-btn"
                    >
                        Annuler
                    </Button>
                    <Button 
                        type="submit" 
                        disabled={saving}
                        className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white rounded-sm"
                        data-testid="submit-btn"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        {isEdit ? 'Enregistrer' : 'Créer le prospect'}
                    </Button>
                </div>
            </form>
        </div>
    );
};
